import { useCallback } from "react";

/**
 * Single chokepoint for the socket `datapushing` contract.
 *
 * The UX redesign refactors heavily around the submit path; everything that
 * sends a pitch goes through here so the payload shape stays byte-identical to
 * the legacy inline emit (spec ① hard invariant: do not change what the
 * recording software receives).
 */

// Floor a float-seconds video position to "HH:MM:SS" — exact replica of the
// legacy `calculateTimeStamp`. The raw float is the precision seam for spec ③
// (P1 will send it as an additive field); the wire value stays floored here.
export const formatVideoTime = (timeSec) => {
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  // The player can briefly report a negative/NaN position near live-edge or
  // seek rebounds; without this clamp `pad(-1)` renders "0-1" (→ "0-1:0-1:0-18").
  const t = Math.max(0, timeSec || 0);
  const h = Math.floor(t / 3600);
  const rem = t % 3600;
  const m = Math.floor(rem / 60);
  const s = Math.floor(rem % 60);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// Build the exact `datapushing` payload. Order and field names are part of the
// contract and are asserted in usePitchSubmit.test.js.
export const buildPitchPayload = (x, y, videoTimeSec) => ({
  KZoneY: x - 45,
  KZoneZ: 74.22 - y,
  VideoTime: formatVideoTime(videoTimeSec),
});

/**
 * @param {object}   opts
 * @param {object}   opts.socket       socket.io client
 * @param {object}   opts.playerRef    ref to the active ReactPlayer
 * @param {Function} [opts.onUnmatched] called with the payload when the ack reports status:false
 * @param {Function} [opts.onSent]      called with the payload on a successful ack
 * @param {Function} [opts.onError]     called when submit cannot proceed
 * @returns {(x:number, y:number) => void} submitPitch
 */
export const usePitchSubmit = ({ socket, playerRef, onUnmatched, onSent, onError }) =>
  useCallback(
    (x, y) => {
      try {
        // Read at the actual click/seek position, not ReactPlayer's 200ms-old
        // progress cache. BasePlayer publishes only the current ready session.
        const videoTimeSec = playerRef.current?.getCurrentTime?.();
        if (!Number.isFinite(videoTimeSec)) throw new Error("Video is not ready");
        const payload = buildPitchPayload(x, y, videoTimeSec);
        socket.emit("datapushing", payload, (res) => {
          if (res && res.status === false) onUnmatched?.(payload);
          else if (res) onSent?.(payload);
        });
      } catch (e) {
        if (onError) onError(e);
        else throw e;
      }
    },
    [socket, playerRef, onUnmatched, onSent, onError]
  );
